import { Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Envelope } from '../../interfaces/envelopes/envelope';
import { ActivatedRoute, Router } from '@angular/router';
import { EnvelopeRepositoryService } from '../../services/envelopes/envelope-repository.service';
import { BehaviorSubject, debounceTime, filter, first, firstValueFrom, forkJoin, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { eBusinessSearchCriteriaTypeString, eOrderStatusType, eOrderType, ePartyType, eResultState } from '../../enums';
import { EnvelopeOrder } from '../../interfaces/envelopes/envelope-order';
import { Order } from '../../interfaces/orders/order';
import cytoscape, { ElementsDefinition, NodeDefinition, NodeSingular, SingularElementReturnValue, StylesheetJson } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { Utilities } from '../../utilities';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { base64StringToBlob } from 'blob-util';
import { Party } from '../../interfaces/parties/party';
import { PartyUIConfiguration } from '../../interfaces/ui-configurations/party-ui-configuration';
import { UltimateBeneficialOwnershipStructure } from '../../interfaces/ultimate-beneficial-ownership-structure/ultimate-beneficial-ownership-structure';
import { ParsedBusinessReportEntity } from '../../interfaces/business-report-parsed-results/parsed-business-report-entity';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { MatDialog } from '@angular/material/dialog';
import { UltimateBeneficialOwnershipDialogComponent } from '../ultimate-beneficial-ownership-dialog/ultimate-beneficial-ownership-dialog.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { OrderUIConfiguration } from '../../interfaces/ui-configurations/order-ui-configuration';
import { UIConfigurationService } from '../../services/ui-configurations/ui-configuration.service';
import { DatePipe } from '@angular/common';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

export interface TreeNode {
  parent?: TreeNode;
  children?: TreeNode[];

  orderGroupID: string,
  businessReportOrder: Order;
  businessReportParsedResults?: ParsedBusinessReportEntity;
  uboOrder?: Order;
  pepsAndSanctionsOrder?: Order;
}

export interface TreeTab {
  label: string;
  shareholder?: UltimateBeneficialOwnershipStructure;
  parentNode?: TreeNode;
  node?: TreeNode;
  graphicalNodeID: string | undefined;
}

export interface OwnershipRecord {
  label: string;
  percentage: number;
}

export interface EnvelopeOrderGroup {
  orderGroupID: string;
  orders: Order[];
}
@Component({
    selector: 'lib-hub-report',
    templateUrl: './hub-report.component.html',
    styleUrl: './hub-report.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HubReportComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  static currentID: number = 1;
  @Input() protected isAdmin = false;

  protected tabIndex = 0;
  protected loading = false;

  protected selectedOrderGroupID: string | undefined;
  protected selectedNode: any;

  protected envelope: Envelope | undefined;
  protected envelope$ = new BehaviorSubject<Envelope | undefined>(undefined);

  protected root: TreeNode | undefined;
  protected pulseNodes: string[] = [];
  protected tree$ = new Subject<ElementsDefinition>();

  protected ownershipRecords: OwnershipRecord[] = [];
  protected outstandingOwnership: number = 0;
  protected tabs: TreeTab[] = [];

  protected orders: Order[] = [];
  protected envelopeOrderGroups: EnvelopeOrderGroup[] = [];
  protected businessReportEnvelopeOrders: EnvelopeOrder[] = [];

  protected hasPepsAndSanctions = false;
  protected pepsAndSanctionsParties$ = new BehaviorSubject<Party[]>([]);

  protected businessReportResults = new Map<string, BehaviorSubject<ParsedBusinessReportEntity | undefined>>();
  protected uboOrderConfiguration!: OrderUIConfiguration;

  private cy!: cytoscape.Core;
  private layout = {
    name: 'dagre',
    rankDir: 'TB',
    rankSep: 40,
    edgeSep: 6,
    ranker: 'tight-tree',
    nodeDimensionsIncludeLabels: true
  } as cytoscape.LayoutOptions;

  private get primaryColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#0072AE';
  }

  private buildStyle(): StylesheetJson {
    const primary = this.primaryColor;
    return [
      {
        selector: 'node',
        style: {
          'background-color': primary,
          label: 'data(label)',
          height: 40,
          shape: 'round-rectangle',
          color: '#fff'
        }
      },
      {
        selector: '.selected-node',
        style: {
          'underlay-color': '#000000',
          'underlay-padding': 10,
          'underlay-opacity': 0.25
        }
      },
      {
        selector: '.center-center',
        style: {
          'text-valign': 'center',
          'text-halign': 'center'
        }
      },
      {
        selector: 'edge.round-taxi',
        style: {
          'curve-style': 'round-taxi',
          'taxi-direction': 'downward',
          'taxi-turn': 20,
          'taxi-turn-min-distance': 5,
          'taxi-radius': 10,
          'target-arrow-shape': 'triangle',
          'target-arrow-color': 'lightgray',
          'line-color': 'lightgray',
          'color': '#000'
        }
      },
      {
        selector: '.highlighted-edge',
        style: {
          'width': 5,
          'line-color': primary,
          'target-arrow-color': primary
        }
      }
    ] as StylesheetJson;
  }

  private onDestroy$ = new Subject<void>();
  private onResize$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private repo: EnvelopeRepositoryService,
    private searchResultsRepo: SearchResultsRepositoryService,
    private router: Router,
    private ordersRepo: OrderRepositoryService,
    private dialog: MatDialog,
    private uiConfigurationService: UIConfigurationService,
    private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.envelope = this.repo.selectedEnvelope;
    this.repo.selectedEnvelope = undefined;

    this.route.paramMap.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(params => {
      const envelopeID = params.get('envelopeID')!;
      this.initialize(this.envelope?.id ?? envelopeID);
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initializeGraph() {
    const treePromise = firstValueFrom(this.tree$.pipe(
      filter(tree => !!tree)));

    cytoscape.use(dagre);
    this.cy = cytoscape({
      container: document.getElementById('order_tree'),
      elements: treePromise,
      style: this.buildStyle(),
      layout: this.layout,
      boxSelectionEnabled: false,
      minZoom: 0.1
    });

    this.cy.ready(() => {
      if (this.getDepth(this.root) < 3) {
        this.cy.zoom(1.5);
      } else {
        this.cy.fit(undefined, 15);
      }
      this.cy.center();

      this.cy.nodes().forEach(node => {
        const nodeWidth = this.getLabelWidth(node);
        node.style({ width: nodeWidth });
      });

      this.pulseNodes.forEach(id => {
        const pulseLoop = (pulse: boolean) => {
          const node = this.cy.nodes().$id(id);

          node?.animate({
            style: { 'background-color': pulse ? '#C6DBEA' : this.primaryColor }
          },
            {
              duration: 1250,
              complete: () => timer(pulse ? 0 : 1500).subscribe(() => {
                if (this.pulseNodes.find(inner => inner === id)) {
                  pulseLoop(!pulse);
                }
              })
            });
        };

        pulseLoop(true);
      });
    });

    this.cy.on('tap', 'node', event => {
      let node = this.cy.$id(event.target.id());
      this.onSelectionChange(node, true);
    });

    this.cy.on('tap', event => {
      // on background click, unhighlight everythin
      if (event.target === this.cy) {
        this.selectedNode = undefined;

        this.cy.elements().forEach(n => {
          n.removeClass('selected-node');
          n.removeClass('highlighted-edge');
        });
      }
    });

    // cxttap is right click, reset the graph on right click
    this.cy.on('cxttap', event => {
      if (event.target === this.cy) {
        if (this.getDepth(this.root) < 3) {
          this.cy.zoom(1.5);
        } else {
          this.cy.fit(undefined, 15);
        }
        this.cy.center();
      }
    });

    this.cy.on('mouseover', 'node', event => {
      const node = event.target;

      if (!this.selectedNode) {
        this.walkTreeEdges(node.id(), edge => {
          edge.addClass('highlighted-edge');
        });
      }
    });

    this.cy.on('mouseout', 'node', event => {
      const node = event.target;

      if (!this.selectedNode) {
        this.walkTreeEdges(node.id(), edge => {
          edge.removeClass('highlighted-edge');
        });
      }
    });

    this.onResize$.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(250))
    .subscribe(() => {
      this.cy.center();
    });
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.onResize$.next();
  }
  
  private onSelectionChange(node: cytoscape.CollectionReturnValue | NodeSingular, updateIndex: boolean) {
    this.cy.elements('node').forEach(n => {
      n.removeClass('selected-node');
    });

    if (this.selectedNode === node) {
      this.selectedNode = undefined;
    } else {
      this.cy.elements('edge').forEach(edge => {
        edge.removeClass('highlighted-edge');
      });

      node.addClass('selected-node');
      this.selectedNode = node;

      if (updateIndex) {
        let index = this.tabs.findIndex(tab => tab.graphicalNodeID === node.id());

        if (index === -1) {
          index = this.tabs.findIndex(tab => tab.node?.businessReportOrder?.orderGroupID === node.data().orderGroupID);
        }

        index += this.orders.some(o => o.orderTypeID === eOrderType.pepsAndSanctions) ? 2: 1;
        this.tabIndex = index;
      }

      this.walkTreeEdges(node.id(), edge => edge.addClass('highlighted-edge'));
    }

    const pulseEntryIndex = this.pulseNodes.findIndex(id => node.id() === id);

    if (pulseEntryIndex !== -1) {
      this.pulseNodes.splice(pulseEntryIndex, 1);
      node.stop();
    }

    node?.style({
      'background-color': this.primaryColor
    });
  }

  private initialize(envelopeID: string) {
    this.initializeGraph();
    this.loading = true;

    (this.envelope ? of(this.envelope) : this.repo.getEnvelope(envelopeID)).pipe(
      tap(envelope => {
        this.envelope = envelope;
        this.envelope$.next(this.envelope);
        this.businessReportEnvelopeOrders = this.envelope?.envelopeOrders
          .filter(eo => eo.orderType === eOrderType.businessReport) ?? [];
        this.businessReportEnvelopeOrders.forEach(eo => this.businessReportResults.set(eo.orderID, new BehaviorSubject<ParsedBusinessReportEntity | undefined>(undefined)));
      }),
      switchMap(envelope => this.repo.getOrders(envelope.id))
    ).subscribe(orders => {
      this.uiConfigurationService.getOrderUIConfigurationByValues({
        country: orders.at(0)?.country ?? 'CA',
        jurisdiction: this.envelope?.jurisdiction ?? orders.at(0)?.jurisdiction ?? '',
        orderTypeID: eOrderType.ultimateBeneficialOwnershipReport
      }).subscribe(config => this.uboOrderConfiguration = config);

      this.orders = orders;
      this.groupOrders();
      this.groupPepsAndSanctionsParties();

      this.root = this.buildOrderTree();
      this.buildOwnershipRecordsForTree();

      //getting the business report results and passing them down to their components
      forkJoin(orders
        .filter(o => o.orderTypeID === eOrderType.businessReport && o.searchResult?.resultState.id === eResultState.parsedDocument)
        .map(o => this.searchResultsRepo.getBusinessReportParsedResults(o.id))
      ).subscribe(results => {
        results.filter(res => !!res.number).forEach(res => {
          const order = this.orders.find(o => o.orderTypeID === eOrderType.businessReport && Utilities.removeNonAlphaNumericChars(o.businessReportCriteria?.number) === Utilities.removeNonAlphaNumericChars(res.number));
          const subject$ = this.businessReportResults.get(order?.id ?? '');
          subject$?.next(res);
        });

        this.buildGraphicalTree(this.root!);
        this.tabIndex = 0;
        this.loading = false;
      });
    });
  }

  private groupOrders() {
    this.envelopeOrderGroups = [];

    this.orders.forEach(o => {
      // peps and sanctions are handled separately, condensed into one
      if (o.orderTypeID === eOrderType.pepsAndSanctions) {
        this.hasPepsAndSanctions = true;

        return;
      }

      let entry = this.envelopeOrderGroups.find(eog => eog.orderGroupID === o.orderGroupID);

      if (!entry) {
        entry = {
          orderGroupID: o.orderGroupID,
          orders: [o]
        };

        this.envelopeOrderGroups.push(entry);
      } else {
        entry.orders.push(o);
      }
    });

    this.envelopeOrderGroups.forEach(eog => {
      const getOrderType = (orderTypeID: string) => {
        switch (orderTypeID) {
          case eOrderType.businessReport: return 1;
          case eOrderType.ultimateBeneficialOwnershipReport: return 2;
          default: return 1000;
        }
      };

      eog.orders = eog.orders.sort((a, b) => getOrderType(a.orderTypeID) - getOrderType(b.orderTypeID));
    });
  }

  private buildOrderTree(): TreeNode | undefined {
    const rootEnvelopeOrder = this.envelope?.envelopeOrders.find(eo => !eo.parentEnvelopeOrderID && eo.orderType === eOrderType.businessReport);

    if (!rootEnvelopeOrder) {
      return;
    }

    return this.buildOrderTreeNode(rootEnvelopeOrder);
  }

  private buildOrderTreeNode(businessReportEnvelopeOrder: EnvelopeOrder): TreeNode {
    const root: TreeNode = {
      orderGroupID: this.orders.find(o => o.orderGroupID === businessReportEnvelopeOrder.orderGroupID &&
        o.orderTypeID === eOrderType.businessReport)!.orderGroupID,
      businessReportOrder: this.orders.find(o => o.orderGroupID === businessReportEnvelopeOrder.orderGroupID &&
        o.orderTypeID === eOrderType.businessReport)!,
      uboOrder: this.orders.find(o => o.orderGroupID === businessReportEnvelopeOrder.orderGroupID &&
        o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport)!,
      pepsAndSanctionsOrder: this.orders.find(o => o.orderGroupID === businessReportEnvelopeOrder.orderGroupID &&
        o.orderTypeID === eOrderType.pepsAndSanctions)!
    };

    const childBusinessReportEnvelopeOrders = this.envelope?.envelopeOrders.filter(eo => eo.parentEnvelopeOrderID === businessReportEnvelopeOrder.id &&
      eo.orderType === eOrderType.businessReport);
    root.children = childBusinessReportEnvelopeOrders?.map(eo => this.buildOrderTreeNode(eo));
    root.children?.forEach(child => child.parent = root);

    this.businessReportResults.get(root.businessReportOrder?.id)?.pipe(
      first(res => res?.name !== undefined)
    ).subscribe(res => root.businessReportParsedResults = res);

    return root;
  }

  private buildGraphicalTree(root: TreeNode) {
    const result: ElementsDefinition = { nodes: [], edges: [] };
    this.tabs = [];
    this.buildGraphicalTreeNode(root, result, 100);
    this.tree$.next(result);
  }

  private buildGraphicalTreeNode(root: TreeNode, result: ElementsDefinition, percent: number | undefined): NodeDefinition {
    if (percent === undefined) {
      percent = 100;
    }

    const modifier = percent * 0.01;
    const parsedResults = this.businessReportResults.get(root.businessReportOrder!.id)?.value;
    const number = root.businessReportOrder?.businessReportCriteria?.number ??
      parsedResults?.number ??
      root.uboOrder?.ultimateBeneficialOwnershipRequest?.entityNumber;
    const name = parsedResults?.translatedName ??
      parsedResults?.name ??
      root.parent?.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders.find(s => s.id === root?.parent?.businessReportOrder?.businessReportCriteria?.ultimateBeneficialOwnershipShareholderID 
        || s.party.busNumber === number)?.party.busName;
    const label = root !== this.root &&
      (root.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.length ?? 0) === 0 ? `${name} (${percent.toFixed(2)}%)` : name;
    const shareholder = root.parent?.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders.find(s => s.id === root.businessReportOrder?.businessReportCriteria?.ultimateBeneficialOwnershipShareholderID ||
      Utilities.removeNonAlphaNumericChars(s.party.busNumber) === Utilities.removeNonAlphaNumericChars(number));

    const parentNode: NodeDefinition = {
      data: { id: (HubReportComponent.currentID++).toString(), label: label, number: number, shareholder: shareholder },
      classes: 'center-center'
    };

    // flashing if there is a complete business report and no completed ubo order
    if (root.businessReportOrder?.orderStatusTypeID === eOrderStatusType.complete &&
      (!root.uboOrder ||
        root.uboOrder.orderStatusTypeID === eOrderStatusType.draft)) {
      this.pulseNodes.push(parentNode.data.id!);
    }

    result.nodes.push(parentNode);
    this.tabs.push({
      label: name ?? '',
      parentNode: root.parent,
      node: root,
      graphicalNodeID: parentNode.data.id,
      shareholder: shareholder
    });

    root.children?.filter(child => child.businessReportOrder).forEach(child => {
      const shareholder = root.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.find(s => s.id === child.businessReportOrder?.businessReportCriteria?.ultimateBeneficialOwnershipShareholderID ||
        Utilities.removeNonAlphaNumericChars(s.party.busNumber) === Utilities.removeNonAlphaNumericChars(child.businessReportOrder?.businessReportCriteria?.number));
      const childNode = this.buildGraphicalTreeNode(child, result, (shareholder?.ownershipPercentage ?? 1) * modifier);

      result.edges.push({
        data: { source: parentNode.data.id!.toString(), target: childNode.data.id!.toString() },
        classes: 'round-taxi'
      });
    });

    if(root.uboOrder?.orderStatusTypeID === eOrderStatusType.complete) {
      root.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.forEach(s => {
        if (result.nodes.some(n => n.data['shareholder']?.id === s.id)) {
          return;
        }

        const childNode = {
          data: { id: (HubReportComponent.currentID++).toString(), orderGroupID: root.uboOrder?.orderGroupID, label: `${this.getShareholderLabel(s, false)} (${((s.ownershipPercentage ?? 1) * modifier).toFixed(2)}%)` },
          classes: 'center-center'
        };

        if (s.party.partyTypeID === ePartyType.businessBeneficialOwner || s.party.partyTypeID === ePartyType.businessShareholder) {
          this.tabs.push({
            label: this.getShareholderLabel(s),
            shareholder: s,
            graphicalNodeID: childNode.data.id,
            parentNode: root
          });

          this.pulseNodes.push(childNode.data.id);
        }

        result.nodes.push(childNode);
        result.edges.push({
          data: { source: parentNode.data.id!.toString(), target: childNode.data.id!.toString() },
          classes: 'round-taxi'
        });
      });
    }

    return parentNode;
  }

  protected buildOwnershipRecordsForTree() {
    this.ownershipRecords = [];
    this.outstandingOwnership = 100;
    this.buildOwnershipRecordsForNode(this.root, 100);

    this.ownershipRecords = this.ownershipRecords.sort((a, b) => b.percentage - a.percentage);
  }

  private buildOwnershipRecordsForNode(root: TreeNode | undefined, percent: number | undefined) {
    if (!root) {
      return;
    } else if (percent === undefined) {
      percent = 100;
    }

    const modifier = percent * 0.01;

    // If there are indiviudal shareholders, we can add or update records
    root.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.filter(s => s.party.partyTypeID === ePartyType.individualBeneficialOwner || s.party.partyTypeID === ePartyType.individualShareholder).forEach(s => {
      let record = this.ownershipRecords.find(e => e.label.toLowerCase() === this.getShareholderLabel(s).toLocaleLowerCase());
      this.outstandingOwnership -= modifier * (s.ownershipPercentage ?? 1);

      if (record) {
        record.percentage += modifier * (s.ownershipPercentage ?? 1);
      } else {
        record = {
          label: this.getShareholderLabel(s),
          percentage: modifier * (s.ownershipPercentage ?? 1)
        };

        this.ownershipRecords.push(record);
      }
    });

    root.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.filter(s => s.party.partyTypeID === ePartyType.businessBeneficialOwner || s.party.partyTypeID === ePartyType.businessShareholder).forEach(s => {
      const child = root.children?.find(c => c.businessReportOrder?.businessReportCriteria?.ultimateBeneficialOwnershipShareholderID === s.id ||
        Utilities.removeNonAlphaNumericChars(c.businessReportOrder?.businessReportCriteria?.number) === Utilities.removeNonAlphaNumericChars(s.party.busNumber));

      if ((child?.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders?.length ?? 0) > 0) {
        this.buildOwnershipRecordsForNode(child, (s.ownershipPercentage ?? 1) * modifier);
      } else {
        let record = this.ownershipRecords.find(e => e.label.toLowerCase() === this.getShareholderLabel(s).toLocaleLowerCase());

        if (record) {
          record.percentage += modifier * (s.ownershipPercentage ?? 1);
        } else {
          record = {
            label: this.getShareholderLabel(s),
            percentage: modifier * (s.ownershipPercentage ?? 1)
          };

          this.ownershipRecords.push(record);
        }
      }
    });
  }

  private getLabelWidth(node: any) {
    const ctx = document.createElement('canvas').getContext("2d");
    const fStyle = node.pstyle('font-style').strValue;
    const size = node.pstyle('font-size').pfValue + 'px';
    const family = node.pstyle('font-family').strValue;
    const weight = node.pstyle('font-weight').strValue;
    const label = node.data('label');

    if (!ctx) {
      return label.length;
    }

    ctx.font = fStyle + ' ' + weight + ' ' + size + ' ' + family;
    return (ctx?.measureText(label).width ?? label.length) + 20;
  }

  protected hasRegistryDocument(order: Order): boolean {
    return !!order.searchResult?.document;
  }

  protected hasRegHubDocument(order: Order): boolean {
    return order.jurisdiction != 'NS' && order.jurisdiction != 'PE' && !!order.searchResult?.summaryDocument;
  }

  protected openRegistryDocument(order: Order) {
    this.searchResultsRepo.getSearchResultDocument(order.id)
      .subscribe(result => {
        var mimeType = "application/pdf";

        switch (result.documentTypeID) {
          case "Text":
            mimeType = "text/plain";
            break;
          default:
            mimeType = "application/pdf";
        }

        const blob = base64StringToBlob(result.base64, mimeType);
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL, '_blank');
      });
  }

  protected openRegHubDocument(order: Order) {
    this.searchResultsRepo.getSearchResultSummaryDocument(order.id)
      .subscribe(result => {
        const blob = base64StringToBlob(result.base64, "application/pdf");
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL, '_blank');
      });
  }

  protected viewOrder(order: Order, newTab: boolean = true) {
    if (newTab) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['orders', order.id])
      );

      window.open(url, '_blank');
    } else {
      this.router.navigate(['orders', order.id]);
    }
  }

  walkTreeEdges(rootID: string | undefined, func: (edge: SingularElementReturnValue) => void) {
    if (!rootID) {
      return;
    }

    const edges = this.cy.elements(`edge[source = "${rootID}"]`);
    edges.forEach(func);

    edges.forEach(edge => {
      const targetID = edge.target().id();
      this.walkTreeEdges(targetID, func);
    });
  }

  private groupPepsAndSanctionsParties() {
    const parties: Party[] = [];

    this.orders
      .filter(o => o.orderTypeID === eOrderType.pepsAndSanctions && o.orderStatusTypeID === eOrderStatusType.complete)
      .sort((a, b) => new Date(a.orderStatusUpdatedDate!).getTime() - new Date(b.orderStatusUpdatedDate!).getTime())
      .forEach(o => {
        o.parties?.forEach(outer => {
          if (!parties.some(inner => `${outer.firstName}${outer.middleName}${outer.lastName}${outer.busName}${outer.busNumber}` ===
            `${inner.firstName}${inner.middleName}${inner.lastName}${inner.busName}${inner.busNumber}`)) {
            parties.push(outer);
          }
        });
      });

    this.pepsAndSanctionsParties$.next(parties
      .sort((a, b) => (b.pepRecords?.length ?? 0) - (a.pepRecords?.length ?? 0))
      .sort((a, b) => (b.sanctionRecords?.length ?? 0) - (a.sanctionRecords?.length ?? 0)));
  }

  protected getPepsAndSanctionsPartiesUiConfiguration(): PartyUIConfiguration {
    return {
      autoCapitalizeFields: false,
      showEstate: false,
      showGeneration: false,
      showRegistryCode: false,
      showDateOfBirth: true,
      showPEPS: true,
      showSanctions: true,
      hasContactDetails: false,
      contactDetailsConfiguration: null,
      allowAddingPartyLinks: false,
      partyLinkAdditionTerm: '',
      allowDeletingOldPartyLinks: false,
      allowDeletingNewPartyLinks: false,
      partyLinkDeletionTerm: '',
      showRegistrationNumber: false,
      showJobTitle: false,
      individualOnly: false,
      hidePartyNumber: false,
      hidePartyType: false
    };
  }

  private getDepth(root: TreeNode | undefined): number {
    if (!root) {
      return 0;
    }

    if (!root.children || root.children.length === 0) {
      return 1;
    } else {
      return 1 + Math.max(...root.children.map(child => this.getDepth(child)));
    }
  }

  protected downloadHubReport() {
    if (!this.envelope?.document) {
      return;
    }

    this.repo.getEnvelopeDocument(this.envelope.id, this.envelope?.document).subscribe(doc => {
      const blobUrl = URL.createObjectURL(doc);
      window.open(blobUrl, '_blank');
    });
  }

  protected getShareholderLabel(shareholder: UltimateBeneficialOwnershipStructure | undefined, showDateOfBirth = true) {
    if (showDateOfBirth === true) {
      return this.getLabel(shareholder?.party.firstName, shareholder?.party.middleName, shareholder?.party.lastName, shareholder?.party.busName, shareholder?.party.busNumber, shareholder?.party.dateOfBirth);
    } else {
      return this.getLabel(shareholder?.party.firstName, shareholder?.party.middleName, shareholder?.party.lastName, shareholder?.party.busName, shareholder?.party.busNumber);
    }
  }

  protected getLabel(first: string | null | undefined, middle: string | null | undefined, last: string | null | undefined, busName: string | null | undefined, busNum?: string | null | undefined, dateOfBirth?: Date | null | undefined) {
    let result = '';

    if (first) {
      result += first;
    }

    if (middle) {
      result += ` ${middle}`;
    }

    if (last) {
      result += ` ${last}`;
    }

    if (busName) {
      result += ` ${busName}`;
    }

    if (!busName && busNum) {
      result += ` ${busNum}`;
    }

    if (dateOfBirth) {
      result += ` (${this.datePipe.transform(dateOfBirth, 'yyyy-MM-dd')})`
    }

    return result.trim();
  }

  getBusinessReportResultsSubject(orderID: string) {
    return this.businessReportResults.get(orderID);
  }

  protected shouldShowBusinessReportAction(tab: TreeTab | undefined) {
    return !tab?.node?.businessReportOrder;
  }

  protected isUsEntity(tab: TreeTab | undefined): boolean {
    return tab?.shareholder?.businessCountry === 'US';
  }

  protected orderBusinessSearch(tab: TreeTab | undefined) {
    if (!tab?.shareholder || !tab.parentNode?.businessReportOrder) {
      return;
    }

    const order = {
      referenceNumber: tab.parentNode.businessReportOrder?.referenceNumber ?? '',
      orderTypeID: eOrderType.businessSearch,
      orderGroupID: tab.parentNode.businessReportOrder.orderGroupID,
      country: tab.parentNode.businessReportOrder.country,
      jurisdiction: '_M',
      businessSearchCriteria: {
        name: tab.shareholder.party.busName,
        businessSearchCriteriaTypeID: eBusinessSearchCriteriaTypeString.Name,
        ultimateBeneficialOwnershipShareholderID: tab.shareholder.id
      },
      searchParameters: {
        searchJurisdictions: [
          { jurisdiction: 'BC' },
          { jurisdiction: 'AB' },
          { jurisdiction: 'SK' },
          { jurisdiction: 'MB' },
          { jurisdiction: 'ON' },
          { jurisdiction: 'QC' },
          { jurisdiction: '_F' },
          { jurisdiction: 'YT' },
          { jurisdiction: 'NT' },
          { jurisdiction: 'NU' },
          { jurisdiction: 'NL' },
          { jurisdiction: 'NB' },
          { jurisdiction: 'NS' },
          { jurisdiction: 'PE' },
        ]
      }
    };

    this.ordersRepo.createNewOrder(order).subscribe(result => {
      if (result.resource.id) {
        this.router.navigate(['orders', 'business', result.resource.id]);
      }
    });
  }

  protected orderBusinessReport(tab: TreeTab | undefined) {
    if (!tab?.shareholder) {
      return;
    }

    const order = {
      referenceNumber: tab.parentNode?.businessReportOrder?.referenceNumber ?? '',
      originalOrderID: tab.parentNode?.businessReportOrder.id,
      country: tab.parentNode?.businessReportOrder.country,
      jurisdiction: tab.shareholder.businessJurisdiction,
      orderTypeID: eOrderType.businessReport,
      businessReportCriteria: {
        number: tab.shareholder.party.busNumber,
        businessName: tab.shareholder.party.busName,
        ultimateBeneficialOwnershipShareholderID: tab.shareholder.id
      }
    };

    this.ordersRepo.createNewOrder(order)
      .subscribe(result => {
        if (result.resource.id) {
          this.router.navigate(['orders', 'business', result.resource.id]);
        }
      });
  }

  protected shouldShowUboAction(tab: TreeTab | undefined) {
    return !tab?.node?.uboOrder;
  }

  protected orderUbo(tab: TreeTab | undefined) {
    const dialogRef = this.dialog.open(UltimateBeneficialOwnershipDialogComponent, {
      data: { order: tab?.node?.businessReportOrder, entity: this.businessReportResults.get(tab?.node?.businessReportOrder?.id ?? '')?.value },
      minWidth: '500px'
    });
  }

  protected shareholderHasBusinessReport(root: TreeNode | undefined, shareholder: UltimateBeneficialOwnershipStructure) {
    return root?.children?.some(c => Utilities.removeNonAlphaNumericChars(c.businessReportOrder?.businessReportCriteria?.number) === Utilities.removeNonAlphaNumericChars(shareholder.party.busNumber)) ?? false;
  }

  protected onSelectedTabChange(event: MatTabChangeEvent) {
    let index = event.index;
    const hasPeps = this.orders.some(o => o.orderTypeID === eOrderType.pepsAndSanctions);

    if ((hasPeps && index === 1) || index === 0) {
      return;
    }

    index -= hasPeps ? 2 : 1;
    const tab = this.tabs.at(index);
    
    if(!tab?.graphicalNodeID) {
      return;
    }

    const node = this.cy.nodes().$id(tab?.graphicalNodeID);
    
    if (this.selectedNode !== node) {
      this.onSelectionChange(node, false);
    }
  }

  protected getOrderStatus(order?: Order) {
    if (!order) {
      return '';
    }

    return this.enumLabels.label('orderStatusType', order.orderStatusTypeID);
  }

  protected getTabOwnershipPercentage(tab: TreeTab) {
    return tab?.shareholder?.ownershipPercentage ??
      tab?.parentNode?.uboOrder?.ultimateBeneficialOwnershipRequest?.shareholders.find(s => s.party.busNumber === tab.node?.businessReportOrder?.businessReportCriteria?.number)?.ownershipPercentage;
  }

  protected isValidated(tab: TreeTab) {
    return tab?.shareholder?.isValidated ?? false;
  }
}